import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Power } from 'lucide-react';
import { departmentsApi } from '../../../api/admin/departments';
import { genericDocumentApi } from '../../../api/generic-documents';
import { ApiError } from '../../../api/client';
import { PageHeader } from '../../../components/PageHeader';
import { CommentsThread } from '../../../components/CommentsThread';
import { DocumentsSection } from '../../../components/DocumentsSection';
import { WorkflowTrackingTab } from '../../../components/WorkflowTrackingTab';
import { DEPARTMENT_STAGES } from '../../../config/workflow-stages';
import { WorkspaceTabs } from '../../../components/WorkspaceTabs';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';

export function DepartmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const departmentId = Number(id);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermission('admin.departments.manage');

  const listQuery = useQuery({ queryKey: ['departments'], queryFn: () => departmentsApi.list() });
  const docsQuery = useQuery({ queryKey: ['department-documents', departmentId], queryFn: () => genericDocumentApi.listFor('department', departmentId) });

  const deactivateMutation = useMutation({
    mutationFn: () => departmentsApi.deactivate(departmentId),
    onSuccess: () => { showSuccess('Department deactivated.'); queryClient.invalidateQueries({ queryKey: ['departments'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to deactivate department.'),
  });

  if (!listQuery.data) return <div className="page"><p className="muted">Loading…</p></div>;
  const d = listQuery.data.data.find((dept) => dept.id === departmentId);
  if (!d) return <div className="page"><p className="muted">Department not found.</p></div>;

  return (
    <div className="page">
      <Link to="/admin/departments">&larr; Back to Departments</Link>
      <PageHeader
        title={d.name}
        actions={canManage && d.is_active && <button className="btn-secondary" onClick={() => deactivateMutation.mutate()}><Power size={16} className="inline-icon" /> Deactivate</button>}
        readOnly={!canManage}
      />
      <p className="muted">{d.code ?? '-'} · {d.is_active ? 'Active' : 'Inactive'}</p>

      <WorkspaceTabs
        tabs={[
          {
            key: 'details',
            label: 'Details',
            content: <CommentsThread entityType="department" entityId={departmentId} />,
          },
          {
            key: 'documents',
            label: 'Documents',
            content: (
              <DocumentsSection
                documents={docsQuery.data?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('department', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('department', departmentId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('department', documentId)}
                queryKeyToInvalidate={['department-documents', departmentId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking',
            label: 'Tracking',
            content: <WorkflowTrackingTab entityType="department" entityId={departmentId} actionStageMap={DEPARTMENT_STAGES} />,
          },
        ]}
      />
    </div>
  );
}
